import axios, { type AxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios'
import qs from 'qs'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '@/stores/auth'
import { useOrganizationStore } from '@/stores/organization'
import i18n from '@/locales'
import { isAdminHost } from '@/lib/host'
import { SOCKET_ID } from '@/lib/socketId'
import { announceApiWrite } from '@/lib/apiWrites'
import type { paths } from './schema'

const JSONAPI_MEDIA_TYPE = 'application/vnd.api+json'
const JSON_MEDIA_TYPE = 'application/json'

// Helper to flatten nested include objects into JSON:API dot notation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function flattenIncludes(obj: Record<string, any>, prefix = ''): string[] {
  return Object.entries(obj).flatMap(([key, value]) => {
    const currentPath = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'object' && value !== null) {
      return [currentPath, ...flattenIncludes(value, currentPath)]
    }
    return value === true ? [currentPath] : []
  })
}

// --- Shared Axios Setup ---

interface InstanceOptions {
  // Tenant clients stamp X-Organization-Id from the org store on every call;
  // the admin client NEVER does — the operator API is cross-org by definition,
  // so tenant context must be structurally impossible, not just absent.
  organizationHeader: boolean
  // Dev tripwire for the admin instance: admin calls are same-origin relative
  // and only the admin. host serves them (routes.rb host gate) — running this
  // instance anywhere else is a wiring bug, not a supported cross-origin call.
  requireAdminHost?: boolean
}

function createAxiosInstance({ organizationHeader, requireAdminHost = false }: InstanceOptions) {
  const instance = axios.create({
  baseURL: Capacitor.isNativePlatform()
    ? (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000')
    : '/',
  withCredentials: true,
  paramsSerializer: {
    serialize: (params) => {
      const processedParams = { ...params }

      // 1. Transform include object into comma-separated string
      if (processedParams.include && typeof processedParams.include === 'object' && !Array.isArray(processedParams.include)) {
        processedParams.include = flattenIncludes(processedParams.include).join(',')
      }

      // 2. Transform sort array into comma-separated string
      if (Array.isArray(processedParams.sort)) {
        processedParams.sort = processedParams.sort.join(',')
      }

      // 3. Transform filter array values into comma-separated strings
      if (processedParams.filter && typeof processedParams.filter === 'object') {
        processedParams.filter = Object.fromEntries(
          Object.entries(processedParams.filter as Record<string, unknown>).map(([key, value]) => [
            key,
            Array.isArray(value) ? value.join(',') : value
          ])
        )
      }

      // Handle the nested structure expected by openapi-fetch
      return qs.stringify(processedParams, {
        arrayFormat: 'comma',
        encode: false,
      })
    }
  }
  })

  // Per-instance refresh queue: concurrent 401s on one instance coalesce into
  // a single /identity/refresh, and retries wait on it.
  let isRefreshing = false
  let failedQueue: { resolve: (value: string | null) => void; reject: (reason: AxiosError | Error) => void }[] = []

  const processQueue = (error: AxiosError | Error | null, token: string | null = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }

  instance.interceptors.request.use(
    (config) => {
      if (import.meta.env.DEV && requireAdminHost && !isAdminHost()) {
        console.warn(`[adminApiClient] ${config.url} called outside the admin host — this cannot work in production`)
      }

      const authStore = useAuthStore()

      if (authStore.token) {
        config.headers.Authorization = authStore.token
      }

      if (organizationHeader) {
        const orgStore = useOrganizationStore()
        if (orgStore.currentOrganizationId) {
          config.headers['X-Organization-Id'] = orgStore.currentOrganizationId
        }
        // Tags the live update this write causes, so this tab drops its own
        // echo (lib/socketId). Tenant calls only — nothing else is broadcast.
        config.headers['X-Socket-Id'] = SOCKET_ID
      }

      config.headers['X-Locale'] = i18n.global.locale.value

      return config
    },
    (error) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response) => {
      // A tenant write that succeeded — told once, here, to whatever follows
      // this tab's own writes (lib/apiWrites: the sidebar's badge). Tenant
      // calls only: the identity flow writes nothing a badge counts.
      if (organizationHeader) announceApiWrite(response.config.method, response.config.url)
      return response
    },
    async (error) => {
      const authStore = useAuthStore()
      const originalRequest = error.config

      if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/identity/')) {
        if (isRefreshing) {
          return new Promise<string | null>(function(resolve, reject) {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            if (token) originalRequest.headers.Authorization = token
            return instance(originalRequest)
          }).catch(err => {
            return Promise.reject(err)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        const success = await authStore.tryRefreshToken()

        if (success) {
          processQueue(null, authStore.token)
          originalRequest.headers.Authorization = authStore.token
          isRefreshing = false
          return instance(originalRequest)
        } else {
          processQueue(error, null)
          isRefreshing = false
          return Promise.reject(error)
        }
      }
      return Promise.reject(error)
    }
  )

  return instance
}

const axiosInstance = createAxiosInstance({ organizationHeader: true })
const adminAxiosInstance = createAxiosInstance({ organizationHeader: false, requireAdminHost: true })

// --- Generic Client Factory ---

type Path = keyof paths
type Method<P extends Path> = keyof paths[P] & ('get' | 'post' | 'patch' | 'delete' | 'put')

type RequestParams<P extends Path, M extends Method<P>> = 
  paths[P][M] extends { parameters: infer PARAMS } ? PARAMS : never

type RequestBody<P extends Path, M extends Method<P>, T extends string> =
  paths[P][M] extends { requestBody: { content: { [K in T]: infer B } } } ? B : never

// Bodyless writes (e.g. POST …/resend_invite): RequestBody resolves to
// `never`, which would make the data parameter uncallable — map it to
// `undefined` so callers pass exactly that.
type BodyOf<P extends Path, M extends Method<P>, T extends string> =
  [RequestBody<P, M, T>] extends [never] ? undefined : RequestBody<P, M, T>

type ResponseData<P extends Path, M extends Method<P>, T extends string> =
  paths[P][M] extends { responses: { 200: { content: { [K in T]: infer R } } } } ? R :
  paths[P][M] extends { responses: { 201: { content: { [K in T]: infer R } } } } ? R :
  paths[P][M] extends { responses: { 204: unknown } } ? void : unknown

type RequestConfig<P extends Path, M extends Method<P>> = Omit<AxiosRequestConfig, 'params'> & {
  params?: RequestParams<P, M> extends { query?: infer Q } ? Q : Record<string, unknown>
  path?: RequestParams<P, M> extends { path: infer PATH } ? PATH : Record<string, unknown>
}

function resolvePath(path: string, params?: Record<string, string | number>) {
  if (!params) return path
  let resolved = path
  for (const [key, value] of Object.entries(params)) {
    resolved = resolved.replace(`{${key}}`, String(value))
  }
  return resolved
}

// PathSubset fences a client to a slice of the schema — the admin client can
// only name /admin_api/* paths, so tenant code can't reach operator endpoints
// through it (and vice versa) at the type level.
function createClient<T extends string, PathSubset extends Path = Path>(
  contentType: T,
  instance = axiosInstance,
) {
  const requestConfig = (config?: AxiosRequestConfig): AxiosRequestConfig => ({
    ...config,
    headers: {
      'Content-Type': contentType,
      'Accept': contentType,
      ...config?.headers,
    }
  })

  return {
    get: <P extends PathSubset>(url: P, config?: RequestConfig<P, 'get'>): Promise<AxiosResponse<ResponseData<P, 'get', T>>> => {
      return instance.get(resolvePath(url as string, config?.path as Record<string, string | number>), requestConfig(config))
    },
    post: <P extends PathSubset>(url: P, data: BodyOf<P, 'post', T>, config?: RequestConfig<P, 'post'>): Promise<AxiosResponse<ResponseData<P, 'post', T>>> => {
      return instance.post(resolvePath(url as string, config?.path as Record<string, string | number>), data, requestConfig(config))
    },
    patch: <P extends PathSubset>(url: P, data: RequestBody<P, 'patch', T>, config?: RequestConfig<P, 'patch'>): Promise<AxiosResponse<ResponseData<P, 'patch', T>>> => {
      return instance.patch(resolvePath(url as string, config?.path as Record<string, string | number>), data, requestConfig(config))
    },
    put: <P extends PathSubset>(url: P, data: RequestBody<P, 'put', T>, config?: RequestConfig<P, 'put'>): Promise<AxiosResponse<ResponseData<P, 'put', T>>> => {
      return instance.put(resolvePath(url as string, config?.path as Record<string, string | number>), data, requestConfig(config))
    },
    delete: <P extends PathSubset>(url: P, config?: RequestConfig<P, 'delete'>): Promise<AxiosResponse<ResponseData<P, 'delete', T>>> => {
      return instance.delete(resolvePath(url as string, config?.path as Record<string, string | number>), requestConfig(config))
    }
  }
}

// --- Specialized Clients ---

type AdminPath = Extract<Path, `/admin_api/${string}`>

export const identityClient = createClient(JSON_MEDIA_TYPE)
export const jsonapiClient = createClient(JSONAPI_MEDIA_TYPE)
// The admin panel's client: no X-Organization-Id ever, /admin_api/* paths
// only, same-origin relative (the admin app is only served from the admin.
// host — cross-origin admin calls are deliberately unsupported).
export const adminApiClient = createClient<typeof JSONAPI_MEDIA_TYPE, AdminPath>(JSONAPI_MEDIA_TYPE, adminAxiosInstance)

// Binary GET (map snapshots, etc.): a plain <img src> can't carry the
// in-memory JWT, so images are fetched as authenticated blobs through the
// shared axios instance (headers + refresh interceptor included).
export const fetchImageBlob = <P extends Path>(
  url: P,
  config?: { path?: Record<string, string>; params?: Record<string, string | string[]> },
): Promise<AxiosResponse<Blob>> =>
  axiosInstance.get<Blob>(resolvePath(url as string, config?.path), {
    params: config?.params,
    responseType: 'blob',
    headers: { Accept: 'image/png' },
  })

// Documents (the signed proposal copy, later invoices): same authenticated-blob
// shape as fetchImageBlob — a plain <a href> can't carry the in-memory JWT.
export const fetchPdfBlob = <P extends Path>(
  url: P,
  config?: { path?: Record<string, string> },
): Promise<AxiosResponse<Blob>> =>
  axiosInstance.get<Blob>(resolvePath(url as string, config?.path), {
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  })
